import type { PortfolioCategorySlug } from "./portfolioRoutes";

export type StaticPageMeta = {
  title: string;
  description: string;
  path: string;
  image?: string;
  noIndex?: boolean;
};

/** Static route metadata aligned with original Jekyll pages. */
export const STATIC_PAGE_META: Record<string, StaticPageMeta> = {
  home: {
    path: "/",
    title: "Transforming Visions into Impactful Digital Experiences that Drive Results",
    description:
      "I'm passionate about turning ideas into impactful, user-centered designs that not only look great but deliver results. Whether it's a mobile app, website, or interactive platform, I craft experiences that engage and convert.",
  },
  about: {
    path: "/about",
    title: "Exceptional Digital Products with User-Centered Design",
    description:
      "Discover how I create impactful digital experiences through UX design, prototyping, and front-end development. I transform your ideas into functional, engaging products that drive user interaction and business growth.",
    image: "/images/projects/product1.webp",
  },
  services: {
    path: "/services",
    title: "Exceptional Digital Products with User-Centered Design",
    description:
      "Discover how I create impactful digital experiences through UX design, prototyping, and front-end development. Explore services from design sprints to product development and ongoing support.",
    image: "/images/projects/product1.webp",
  },
  contact: {
    path: "/contact",
    title: "Let's Build Your Next Great Digital Experience Together",
    description:
      "Have a project in mind or need help enhancing your digital presence? Get in touch to discuss how we can turn your ideas into intuitive, impactful designs that drive results.",
  },
  startProject: {
    path: "/start-project",
    title: "Project Configurator — See Your Build",
    description:
      "Pick features, add your brand or existing site URL, and watch a live on-brand preview. Send your configured brief — no copy-pasting required.",
  },
  portfolio: {
    path: "/ux-portfolio",
    title: "Portfolio of Innovative UX Designs & Digital Products",
    description:
      "Explore my portfolio showcasing 15+ years of UX design expertise. From intuitive mobile apps to responsive websites, see projects that highlight my approach to transforming ideas into impactful designs.",
    image: "/images/projects/product1.webp",
  },
  experience: {
    path: "/experience",
    title: "Over 15 Years of UX Design and Product Development Expertise",
    description:
      "Explore my journey as a UX designer with over 15 years of experience in product design, prototyping, and front-end development across SaaS, mobile, and enterprise products.",
  },
  faq: {
    path: "/faq",
    title: "Learn design, performance & product strategy",
    description:
      "Plain-language guides on UX design, Lighthouse, Core Web Vitals, scoping, and shipping — with examples from real client work.",
  },
  testimonials: {
    path: "/testimonials",
    title: "Testimonials",
    description:
      "Reach your business goals with excellent user experience. Read what clients say about product design, prototyping, and delivery on web and mobile projects.",
  },
  search: {
    path: "/search",
    title: "Search",
    description: "Search UXMichal.com for projects, FAQ articles, experience, and pages.",
    noIndex: true,
  },
  offline: {
    path: "/offline",
    title: "Offline",
    description: "You are offline. Reconnect to browse UXMichal.com.",
    noIndex: true,
  },
  notFound: {
    path: "/404",
    title: "Page Not Found",
    description: "The page you are looking for could not be found on UXMichal.com.",
    noIndex: true,
  },
};

export const PORTFOLIO_CATEGORY_META: Record<
  PortfolioCategorySlug,
  Pick<StaticPageMeta, "title" | "description" | "image">
> = {
  "product-design": {
    title: "Product Design",
    description:
      "Creating user-centered digital products from concept to optimization, with a focus on delivering measurable impact.",
    image: "/images/projects/product1.webp",
  },
  "mobile-design": {
    title: "Mobile Design",
    description:
      "Design of interactive mobile apps and games for Android and iOS. Simple, functional, and attractive native apps for all devices.",
    image: "/images/projects/mobile1.webp",
  },
  "web-design": {
    title: "Web Design",
    description:
      "UX design of e-shops, corporate sites, blogs and products. Accessible, responsive and user-friendly experience.",
    image: "/images/projects/web1.webp",
  },
  "motion-design": {
    title: "Motion Design",
    description:
      "Crafting interactive animations that elevate user experience and breathe life into your brand across digital platforms.",
    image: "/images/projects/motion1.webp",
  },
  "branding-and-identity": {
    title: "Product Identity",
    description:
      "Building a strong, distinct brand identity with visual tone, cohesive design language, and lasting brand recognition.",
    image: "/images/projects/identity1.webp",
  },
};
